import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Sun, Moon, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function GratitudeGuide() {
  const navigate = useNavigate();

  const prompts = [
    { category: "People", questions: ["Who made you smile today?", "Who has supported you recently?", "Who do you take for granted that deserves appreciation?"] },
    { category: "Moments", questions: ["What small moment brought you joy today?", "What made you laugh recently?", "What peaceful moment did you experience?"] },
    { category: "Body", questions: ["What can your body do that you're grateful for?", "What sense are you most thankful for today?", "How did your body serve you well today?"] },
    { category: "Growth", questions: ["What challenge taught you something valuable?", "What skill have you developed that you're proud of?", "What mistake led to growth?"] },
    { category: "Simple Things", questions: ["What comfort do you enjoy that others don't have?", "What modern convenience made your day easier?", "What in nature brought you peace?"] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">Week 1 • Day 6 Guide</span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">The Practice of Gratitude</h1>
          <p className="text-xl text-secondary-600">Transform Your Mindset One Thank You at a Time</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Gratitude Changes Everything</h2>
          <p className="text-secondary-700 mb-4">
            Gratitude isn't just politeness—it's a powerful psychological intervention. Studies show that practicing gratitude for just 3 weeks can have lasting effects on happiness levels for up to 6 months. It literally rewires your brain to notice the good.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { stat: "25%", desc: "increase in happiness levels" },
              { stat: "10%", desc: "better sleep quality" },
              { stat: "35%", desc: "reduction in depressive symptoms" }
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-rose-50 rounded-xl">
                <p className="text-3xl font-bold text-rose-600">{item.stat}</p>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl p-6 text-white mb-6">
          <h3 className="font-bold text-lg mb-2">Tonight's Practice</h3>
          <p className="text-rose-100">Find a quiet moment with a cup of tea. Walk through your day mentally. Identify 10 specific things you're grateful for—big or small. Say "thank you" for each one, either aloud or in your heart.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Gratitude Prompts by Category</h2>
          <div className="space-y-4">
            {prompts.map((prompt, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <h3 className="text-lg font-bold text-rose-700 mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5" />{prompt.category}
                </h3>
                <ul className="space-y-2">
                  {prompt.questions.map((q, i) => (
                    <li key={i} className="text-secondary-700 bg-rose-50 p-3 rounded-lg">{q}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-xl font-serif font-bold text-secondary-900 mb-4">Making Gratitude a Daily Habit</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Morning Gratitude", tip: "Before getting out of bed, think of 3 things you're grateful for" },
              { title: "Gratitude Jar", tip: "Write daily gratitudes on slips of paper, read them when you need a boost" },
              { title: "Thank You Notes", tip: "Send one genuine thank you message per week to someone in your life" },
              { title: "Gratitude Walk", tip: "During walks, mentally list things you're grateful for with each step" },
              { title: "Bedtime Reflection", tip: "End each day by reviewing 3 good things that happened" },
              { title: "Photo Gratitude", tip: "Take a photo daily of something you're grateful for" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-rose-50 rounded-xl">
                <h3 className="font-bold text-rose-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default GratitudeGuide;
